import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Button, Space, Tooltip, Modal, Select, Row, Col, Empty, Upload, message } from 'antd';
import { FileTextOutlined, DownloadOutlined, DeleteOutlined, PlusOutlined, CalendarOutlined, UploadOutlined } from '@ant-design/icons';
import Card from '../card/Card';
import { useTheme } from '../../contexts/ThemeContext';
import { GrUploadOption } from 'react-icons/gr';

const { Text } = Typography;

const SynchronizationFileUpload = ({ 
  syncId, 
  syncName = 'Sincronização', 
  budgetFiles = [], // Array de SyncBudgetDto
  isPending = false,
  onBudgetFileCreate,
  onBudgetFileDelete,
  onBudgetFileDownload
}) => {
  const { theme } = useTheme();
  

  useEffect(() => {
    console.log('budgetFiles', budgetFiles);
  }, [budgetFiles]);
  // Estados locais
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [uploadingFile, setUploadingFile] = useState(false);
  const [filterYear, setFilterYear] = useState('all'); // 'all' ou ano específico
  const [selectedFile, setSelectedFile] = useState(null);
  const [loadingStates, setLoadingStates] = useState({}); // Loading individual por arquivo
  // Estados do modal de atualização
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [updatingTargetFile, setUpdatingTargetFile] = useState(null);
  const [updateSelectedYear, setUpdateSelectedYear] = useState(new Date().getFullYear());
  const [updateSelectedFile, setUpdateSelectedFile] = useState(null);
  const [uploadingUpdateFile, setUploadingUpdateFile] = useState(false);
  // Gerar anos disponíveis (últimos 10 anos + próximos 2)
  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 10; i <= currentYear + 2; i++) {
      years.push(i);
    }

    
    return years;
  }, []);

  // Ajustar ano padrão ao abrir o modal de criação
  useEffect(() => {
    if (isModalVisible) {
      const currentYear = new Date().getFullYear();
      const alreadyExists = budgetFiles.some(f => f.year === currentYear);
      setSelectedYear(alreadyExists ? currentYear - 1 : currentYear);
    }
  }, [isModalVisible, budgetFiles]);




  // Filtrar arquivos por ano
  const filteredBudgetFiles = useMemo(() => {
    if (filterYear === 'all') {
      return budgetFiles;
    }
    return budgetFiles.filter(file => file.year === parseInt(filterYear));
  }, [budgetFiles, filterYear]);

  // Função para baixar o arquivo
  const handleDownload = async (budgetFile) => {
    try {
      setLoadingStates(prev => ({ ...prev, [`download-${budgetFile.id}`]: true }));
      if (onBudgetFileDownload) {
        await onBudgetFileDownload(budgetFile);
      } else {
        // Fallback para download direto
        if (budgetFile.fileUrl) {
          const link = document.createElement('a');
          link.href = budgetFile.fileUrl;
          link.download = getFileNameFromUrl(budgetFile.fileUrl);
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    } catch (error) {
      console.error('Erro ao baixar arquivo:', error);
      message.error('Erro ao baixar arquivo');
    } finally {
      setLoadingStates(prev => ({ ...prev, [`download-${budgetFile.id}`]: false }));
    }
  };

  // Função para extrair nome do arquivo da URL
  const getFileNameFromUrl = (url) => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const fileName = pathname.split('/').pop();
      
      if (fileName && (fileName.includes('.xlsx') || fileName.includes('.xls') || fileName.includes('.csv'))) {
        return fileName;
      } else {
        return `planilha-orcamento-${selectedYear}.xlsx`;
      }
    } catch {
      return `planilha-orcamento-${selectedYear}.xlsx`;
    }
  };

  // Handler para remoção de arquivos
  const handleDeleteFile = async (budgetFileId) => {
    try {
      setLoadingStates(prev => ({ ...prev, [budgetFileId]: true }));
      if (onBudgetFileDelete) {
        await onBudgetFileDelete(budgetFileId);
      }
    } catch (error) {
      console.error('Erro ao remover arquivo:', error);
      message.error('Erro ao remover arquivo');
    } finally {
      setLoadingStates(prev => ({ ...prev, [budgetFileId]: false }));
    }
  };

  // Handler para seleção de arquivo
  const handleFileSelect = (file) => {
    // Validar tipo de arquivo
    const isValidType = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                       file.type === 'application/vnd.ms-excel' ||
                       file.type === 'text/csv' ||
                       file.name.endsWith('.xlsx') ||
                       file.name.endsWith('.xls') ||
                       file.name.endsWith('.csv');

    if (!isValidType) {
      message.error('Por favor, selecione apenas arquivos Excel (.xlsx, .xls) ou CSV (.csv)');
      return false;
    }

    // Validar tamanho (10MB)
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('O arquivo deve ter menos de 10MB');
      return false;
    }

    setSelectedFile(file);
    return false; // Impedir upload automático
  };

  // Handler para criar ou atualizar arquivo de orçamento
  const handleCreateBudgetFile = async (budgetFile) => {

    if (budgetFile) {
      setSelectedYear(budgetFile.year);
      setSelectedFile(budgetFile.fileUrl);
    }

    if (!selectedFile) {
      message.error('Por favor, selecione um arquivo');
      return;
    }
    if (!selectedYear) {
      message.error('Por favor, selecione o ano');
      return;
    }

    try {
      setUploadingFile(true);
      let responseData = null;
      // Usar o callback onBudgetFileCreate que já foi passado como prop
      if (onBudgetFileCreate) {
        if (budgetFile) {
          responseData = await onBudgetFileCreate({
            syncId: syncId,
            year: selectedYear,
            file: selectedFile,
            syncBudgetId: budgetFile.id // Usar o ID do arquivo existente para atualização
          });
        } else {
          responseData = await onBudgetFileCreate({
            syncId: syncId,
            year: selectedYear,
            file: selectedFile,
            syncBudgetId: null // Sempre null para novos arquivos
          });
        }
      }

      // Atualizar ou adicionar o arquivo de orçamento na lista
      const existingFileIndex = budgetFiles.findIndex(file => file.id === responseData.response.id);
      if (existingFileIndex !== -1) {
        budgetFiles[existingFileIndex] = responseData.response;
      } else {
        budgetFiles.push(responseData.response);
      }

      // Limpar estados após sucesso
      setIsModalVisible(false);
      setSelectedYear(new Date().getFullYear());
      setSelectedFile(null);
      
    } catch (error) {
      console.error('Erro ao criar ou atualizar arquivo de orçamento:', error);
      // Não mostrar mensagem de erro aqui, deixar o componente pai tratar
    } finally {
      setUploadingFile(false);
    }
  };

  // Abrir modal de atualização
  const openUpdateModal = (budgetFile) => {
    setUpdatingTargetFile(budgetFile);
    setUpdateSelectedYear(budgetFile.year);
    setUpdateSelectedFile(null);
    setIsUpdateModalVisible(true);
  };

  // Handler de seleção de arquivo para atualização
  const handleUpdateFileSelect = (file) => {
    const isValidType = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                       file.type === 'application/vnd.ms-excel' ||
                       file.type === 'text/csv' ||
                       file.name.endsWith('.xlsx') ||
                       file.name.endsWith('.xls') ||
                       file.name.endsWith('.csv');

    if (!isValidType) {
      message.error('Por favor, selecione apenas arquivos Excel (.xlsx, .xls) ou CSV (.csv)');
      return false;
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('O arquivo deve ter menos de 10MB');
      return false;
    }

    setUpdateSelectedFile(file);
    return false;
  };

  // Handler para atualizar arquivo existente
  const handleUpdateBudgetFile = async () => {
    if (!updatingTargetFile) {
      return;
    }

    if (!updateSelectedFile) {
      message.error('Por favor, selecione um arquivo para atualizar');
      return;
    }

    try {
      setUploadingUpdateFile(true);
      let responseData = null;
      if (onBudgetFileCreate) {
        responseData = await onBudgetFileCreate({
          syncId: syncId,
          year: updateSelectedYear,
          file: updateSelectedFile,
          id: updatingTargetFile.id
        });
      }

      if (responseData && responseData.response) {
        const existingFileIndex = budgetFiles.findIndex(file => file.id === responseData.response.id);
        if (existingFileIndex !== -1) {
          budgetFiles[existingFileIndex] = responseData.response;
        } else {
          budgetFiles.push(responseData.response);
        }
      }

      setIsUpdateModalVisible(false);
      setUpdatingTargetFile(null);
      setUpdateSelectedYear(new Date().getFullYear());
      setUpdateSelectedFile(null);
    } catch (error) {
      console.error('Erro ao atualizar arquivo de orçamento:', error);
    } finally {
      setUploadingUpdateFile(false);
    }
  };

  // Ícone do Excel simples
  const ExcelIcon = ({ size = 32, status = 'default' }) => {
    const getStatusColor = () => {
      switch (status) {
        case 'processed': return '#52c41a';
        case 'pending': return '#faad14';
        case 'error': return '#ff4d4f';
        default: return '#1D6F42';
      }
    };

    return (
      <div style={{
        width: size,
        height: size,
        backgroundColor: getStatusColor(),
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative'
      }}>
        <div style={{
          color: 'white',
          fontWeight: 'bold',
          fontSize: size > 28 ? 11 : 9,
          lineHeight: 1
        }}>
          XLS
        </div>
        {status === 'processed' && (
          <div style={{
            position: 'absolute',
            top: -2,
            right: -2,
            width: 8,
            height: 8,
            backgroundColor: '#52c41a',
            borderRadius: '50%',
            border: '1px solid white'
          }} />
        )}
      </div>
    );
  };

  // Renderizar card de arquivo individual
  const renderBudgetFileCard = (budgetFile) => {
    const isProcessed = budgetFile.isProcessed;
    const hasError = budgetFile.message && budgetFile.message.includes('erro');
    
    const status = hasError ? 'error' : (isProcessed ? 'processed' : 'pending');

    return (
      <div key={budgetFile.id} style={{ 
        padding: 16,
        marginBottom: 12,
        backgroundColor: theme.background,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 12
      }}>
        <ExcelIcon 
          size={32} 
          status={status}
        />
        
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Text style={{ 
              fontSize: 14, 
              fontWeight: 500, 
              color: theme.primary
            }}>
             {budgetFile.fileUrl}
            </Text>
           
          </div>
          
          <Text style={{ 
            fontSize: 12, 
            color: theme.textSecondary,
            display: 'block'
          }}>
            Ano: {budgetFile.year}
          </Text>
          
          {budgetFile.message && (
            <Text style={{ 
              fontSize: 12, 
              color: hasError ? '#cf1322' : theme.textSecondary,
              display: 'block',
              fontStyle: 'italic'
            }}>
              {budgetFile.message}
            </Text>
          )}
        </div>
        
        <Space size="small">
          <Tooltip title="Atualizar Arquivo">
            <Button
              type="text"
              loading={loadingStates[`update-${budgetFile.id}`]}
              icon={<GrUploadOption />}
              size="small"
              onClick={() => openUpdateModal(budgetFile)}
              style={{ color: '#1D6F42' }}
            />
          </Tooltip>

              
          <Tooltip title="Baixar arquivo">
            <Button
              type="text"
              loading={loadingStates[`download-${budgetFile.id}`]}
              icon={<DownloadOutlined />}
              size="small"
              onClick={() => handleDownload(budgetFile)}
              style={{ color: '#52c41a' }}
            />
          </Tooltip>
          
          <Tooltip title="Remover arquivo">
            <Button
              type="text"
              loading={loadingStates[budgetFile.id]}
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleDeleteFile(budgetFile.id)}
              style={{ color: '#ff4d4f' }}
            />
          </Tooltip>
        </Space>
      </div>
    );
  };

  return (
    <>
      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileTextOutlined />
            <span style={{ fontSize: 16, fontWeight: 600, color: theme.textSecondary }}>
              Planilhas Orçamentárias
            </span>
          </div>
        }
        style={{ 
          marginBottom: 24,
          borderRadius: 12,
          border: `1px solid ${theme.border}`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}
        bodyStyle={{ padding: '20px' }}
      >
        <div style={{ marginBottom: 16 }}>
          <Text style={{ 
            display: 'block',
            marginBottom: 12,
            color: theme.textSecondary,
            fontSize: 14
          }}>
            Gerencie as planilhas Excel com dados orçamentários por ano
          </Text>
          
          {/* Filtro por ano */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <Text style={{ fontSize: 14, fontWeight: 500 }}>Filtrar por ano:</Text>
            <Select
              value={filterYear}
              onChange={setFilterYear}
              style={{ width: 150 }}
              options={[
                { value: 'all', label: 'Todos os anos' },
                ...availableYears.map(year => ({ value: year.toString(), label: year.toString() }))
              ]}
            />
          </div>
        </div>

        {/* Lista de arquivos */}
        {filteredBudgetFiles.length > 0 ? (
          <div style={{ marginBottom: 16 }}>
            {filteredBudgetFiles.map(renderBudgetFileCard)}
          </div>
        ) : (
          <Empty
            description={
              <div style={{ textAlign: 'center' }}>
                <Text style={{ color: theme.textSecondary }}>
                  {filterYear === 'all' 
                    ? 'Nenhuma planilha orçamentária encontrada' 
                    : `Nenhuma planilha encontrada para ${filterYear}`
                  }
                </Text>
              </div>
            }
            style={{ margin: '20px 0' }}
          />
        )}

        {/* Botão para adicionar nova planilha */}
        <Button
          icon={<PlusOutlined />}
          onClick={() => setIsModalVisible(true)}
          style={{ 
            width: '100%',
            height: 48,
            borderColor: theme.primary,
            color: theme.primary
          }}
        >
          Adicionar Nova Planilha Orçamentária
        </Button>
      </Card>

      {/* Modal para adicionar nova planilha */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CalendarOutlined />
            <span>Nova Planilha Orçamentária</span>
          </div>
        }
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedFile(null);
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setIsModalVisible(false);
            setSelectedFile(null);
          }}>
            Cancelar
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            loading={uploadingFile}
            onClick={handleCreateBudgetFile}
            disabled={!selectedYear}
          >
            Enviar Arquivo
          </Button>
        ]}
        width={600}
        centered
      >
        <div style={{ padding: '20px 0' }}> 
          <Text style={{ 
            display: 'block',
            marginBottom: 16,
            color: theme.textSecondary,
            fontSize: 14
          }}>
            Selecione o ano e faça upload da planilha Excel com dados orçamentários
          </Text>

          <Row gutter={[16, 16]}>
            <Col span={24}>
              <div style={{ marginBottom: 16 }}>
                <Text style={{ 
                  display: 'block',
                  marginBottom: 8,
                  fontWeight: 500
                }}>
                  Ano da Planilha:
                </Text>
                <Select
                  value={selectedYear}
                  onChange={setSelectedYear}
                  style={{ width: '100%' }}
                  options={availableYears
                    .filter((year) => !budgetFiles.some((f) => f.year === year))
                    .map((year) => ({
                      value: year,
                      label: year.toString()
                    }))}
                />
              </div>
            </Col>
            
            <Col span={24}>
              <div style={{ marginBottom: 16 }}>
                <Text style={{ 
                  display: 'block',
                  marginBottom: 8,
                  fontWeight: 500
                }}>
                  Selecionar Arquivo:
                </Text>
                <Upload.Dragger
                  name="file"
                  beforeUpload={handleFileSelect}
                  showUploadList={false}
                  accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                  style={{
                    border: `2px dashed ${selectedFile ? '#52c41a' : theme.border}`,
                    borderRadius: 8,
                    backgroundColor: selectedFile ? '#f6ffed' : 'transparent'
                  }}
                >
                  <div style={{ padding: '20px' }}>
                    <UploadOutlined style={{ fontSize: 48, color: selectedFile ? '#52c41a' : theme.textSecondary, marginBottom: 16 }} />
                    <div style={{ marginBottom: 8 }}>
                      <Text style={{ fontSize: 16, fontWeight: 500 }}>
                        {selectedFile ? selectedFile.name : 'Clique ou arraste o arquivo aqui'}
                      </Text>
                    </div>
                    <Text style={{ color: theme.textSecondary }}>
                      {selectedFile 
                        ? `Arquivo selecionado: ${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(2)} MB)`
                        : 'Suporta XLS, XLSX ou CSV até 10MB'
                      }
                    </Text>
                  </div>
                </Upload.Dragger>
              </div>
            </Col>
          </Row>
        </div>
      </Modal>

      {/* Modal para atualizar planilha */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UploadOutlined />
            <span>Atualizar Planilha Orçamentária</span>
          </div>
        }
        open={isUpdateModalVisible}
        onCancel={() => {
          setIsUpdateModalVisible(false);
          setUpdatingTargetFile(null);
          setUpdateSelectedFile(null);
        }}
        footer={[
          <Button key="cancel-update" onClick={() => {
            setIsUpdateModalVisible(false);
            setUpdatingTargetFile(null);
            setUpdateSelectedFile(null);
          }}>
            Cancelar
          </Button>,
          <Button
            key="submit-update"
            type="primary"
            loading={uploadingUpdateFile}
            onClick={handleUpdateBudgetFile}
            disabled={!updateSelectedYear || !updateSelectedFile}
          >
            Atualizar Arquivo
          </Button>
        ]}
        width={600}
        centered
      >
        <div style={{ padding: '20px 0' }}>
          <Text style={{ 
            display: 'block',
            marginBottom: 16,
            color: theme.textSecondary,
            fontSize: 14
          }}>
            Selecione o ano e faça upload do novo arquivo para substituir a planilha atual
          </Text>

          <Row gutter={[16, 16]}>
            <Col span={24}>
              <div style={{ marginBottom: 16 }}>
                <Text style={{ 
                  display: 'block',
                  marginBottom: 8,
                  fontWeight: 500
                }}>
                  Ano da Planilha:
                </Text>
                <Select
                  value={updateSelectedYear}
                  onChange={setUpdateSelectedYear}
                  disabled={true}
                  style={{ width: '100%' }}
                  options={availableYears.map(year => ({ 
                    value: year, 
                    label: year.toString() 
                  }))}
                />
              </div>
            </Col>

            <Col span={24}>
              <div style={{ marginBottom: 16 }}>
                <Text style={{ 
                  display: 'block',
                  marginBottom: 8,
                  fontWeight: 500
                }}>
                  Selecionar novo arquivo:
                </Text>
                <Upload.Dragger
                  name="file"
                  beforeUpload={handleUpdateFileSelect}
                  showUploadList={false}
                  accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                  style={{
                    border: `2px dashed ${updateSelectedFile ? '#52c41a' : theme.border}`,
                    borderRadius: 8,
                    backgroundColor: updateSelectedFile ? '#f6ffed' : 'transparent'
                  }}
                >
                  <div style={{ padding: '20px' }}>
                    <UploadOutlined style={{ fontSize: 48, color: updateSelectedFile ? '#52c41a' : theme.textSecondary, marginBottom: 16 }} />
                    <div style={{ marginBottom: 8 }}>
                      <Text style={{ fontSize: 16, fontWeight: 500 }}>
                        {updateSelectedFile ? updateSelectedFile.name : 'Clique ou arraste o novo arquivo aqui'}
                      </Text>
                    </div>
                    <Text style={{ color: theme.textSecondary }}>
                      {updateSelectedFile 
                        ? `Arquivo selecionado: ${updateSelectedFile.name} (${(updateSelectedFile.size / 1024 / 1024).toFixed(2)} MB)`
                        : 'Suporta XLS, XLSX ou CSV até 10MB'
                      }
                    </Text>
                  </div>
                </Upload.Dragger>
                {updatingTargetFile && (
                  <div style={{ marginTop: 8 }}>
                    <Text style={{ fontSize: 12, color: theme.textSecondary }}>
                      Arquivo atual: {updatingTargetFile.fileUrl}
                    </Text>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </Modal>
    </>
  );
};

export default SynchronizationFileUpload; 